/**
 * Step 4 — "Connect your stack": Gmail + Calendar status and meeting
 * recorder selection.
 *
 * UX-only: the Gmail card is presentational (real status comes with the
 * OAuth hookup) and recorder selections just live in flow state.
 */
import { useCallback, useMemo } from 'react';

import { BadgeWithDot } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { CARD_BORDER } from '@/lib/base/list-card';
import { SelectableRow } from '@/lib/self-service/components/SelectableRow';
import { SkipButton } from '@/lib/self-service/components/SkipButton';
import { RECORDER_OPTIONS } from '@/lib/self-service/constants';
import { useSelfServiceFlowState } from '@/lib/self-service/flowState';
import { useToggleInArray } from '@/lib/self-service/hooks/useToggleInArray';
import { cx } from '@/utils/cx';

const CARD_CLASSES = cx(CARD_BORDER, 'bg-bg-primary');

interface StackStepProps {
  onContinue: () => void;
}

export function StackStep({ onContinue }: StackStepProps) {
  const { state, updateState } = useSelfServiceFlowState();

  const handleRecorderChange = useCallback(
    (next: string[]) => updateState({ recorders: next }),
    [updateState]
  );
  const handleRecorderToggle = useToggleInArray(
    state.recorders,
    handleRecorderChange
  );

  const recorderOptionsWithLeading = useMemo(
    () =>
      RECORDER_OPTIONS.map(recorder => ({
        ...recorder,
        leading: (
          <img src={recorder.iconSrc} alt='' className='size-7 shrink-0' />
        ),
      })),
    []
  );

  return (
    <div className='flex flex-col gap-6'>
      {/* Gmail + Calendar (presentational until OAuth is wired) */}
      <div
        className={cx(CARD_CLASSES, 'flex items-center justify-between p-4')}
      >
        <div className='flex items-center gap-3'>
          <img
            src='/assets/self-service/icons/gmail.svg'
            alt=''
            className='size-8 shrink-0'
          />
          <div className='flex flex-col'>
            <span className='text-sm font-semibold text-[var(--color-text-primary)]'>
              Gmail + Calendar
            </span>
            <span className='text-sm text-[var(--color-text-tertiary)]'>
              Connected with your Google sign-in
            </span>
          </div>
        </div>
        <BadgeWithDot color='success' size='md'>
          Connected
        </BadgeWithDot>
      </div>

      {/* Meeting recorders */}
      <div className='flex flex-col gap-3'>
        <span className='text-sm font-semibold text-[var(--color-text-primary)]'>
          Meeting recorder
        </span>
        {recorderOptionsWithLeading.map(recorder => (
          <div key={recorder.id} className={CARD_CLASSES}>
            <SelectableRow
              id={recorder.id}
              leading={recorder.leading}
              title={recorder.name}
              subtitle={recorder.description}
              isSelected={state.recorders.includes(recorder.id)}
              onToggle={handleRecorderToggle}
            />
          </div>
        ))}
      </div>

      <div className='flex flex-col gap-4'>
        <Button size='lg' className='w-full' onClick={onContinue}>
          Continue
        </Button>
        <SkipButton onSkip={onContinue} />
      </div>
    </div>
  );
}
